import { create } from 'zustand';
import { sportSeasonDefaultsApi } from '../lib/api';
import type {
  SportSeasonMatrix,
  CoverageStats,
  SportSeasonDefault,
  Season,
  Confidence,
  Gender,
} from '../types/sportSeason';

interface CreateRegionalRowInput {
  sportName: string;
  gender: Gender;
  stateCode: string;
  zip3Prefix: string;
  primarySeason: Season | null;
  confidence: Confidence;
  sanctioned: boolean;
  alsoOffered?: Season[];
  source?: string | null;
  notes?: string | null;
}

interface SportSeasonsState {
  matrix: SportSeasonMatrix;
  coverage: CoverageStats | null;
  loading: boolean;
  error: string | null;

  loadMatrix: () => Promise<void>;
  loadCoverage: () => Promise<void>;
  saveCell: (
    id: string,
    body: Record<string, unknown>,
    opts?: { dryRun?: boolean },
  ) => Promise<{ default: unknown; propagation: unknown }>;

  // Regional override CRUD (zip3Prefix !== null rows). Returns the regional
  // rows for a given (sport, gender, state); callers manage local state — these
  // methods deliberately don't mutate the matrix since the matrix only carries
  // state-wide cells. Create/delete also refresh the matrix; update does not
  // since regionalCount is unaffected by edits.
  fetchRegionalRows: (
    sportName: string,
    gender: Gender,
    stateCode: string,
  ) => Promise<SportSeasonDefault[]>;
  createRegionalRow: (input: CreateRegionalRowInput) => Promise<SportSeasonDefault>;
  updateRegionalRow: (
    id: string,
    patch: Partial<SportSeasonDefault>,
  ) => Promise<SportSeasonDefault>;
  deleteRegionalRow: (id: string) => Promise<void>;
}

export const useSportSeasonsStore = create<SportSeasonsState>((set, get) => ({
  matrix: {},
  coverage: null,
  loading: false,
  error: null,

  loadMatrix: async () => {
    set({ loading: true, error: null });
    try {
      const matrix = await sportSeasonDefaultsApi.matrix();
      set({ matrix, loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load matrix';
      set({ error: message, loading: false });
    }
  },

  loadCoverage: async () => {
    try {
      set({ coverage: await sportSeasonDefaultsApi.coverage() });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load coverage';
      set({ error: message });
    }
  },

  saveCell: async (id, body, opts) => {
    const result = await sportSeasonDefaultsApi.update(id, body, opts?.dryRun);
    if (!opts?.dryRun) await get().loadMatrix();
    return result;
  },

  fetchRegionalRows: async (sportName, gender, stateCode) => {
    // The backend list endpoint returns both state-wide (zip3Prefix=null) and
    // regional rows; we filter client-side. Page size has to cover every
    // regional row for the triple — 500 is well above any realistic count.
    const res = await sportSeasonDefaultsApi.list({
      sportName,
      gender,
      stateCode,
      pageSize: 500,
    });
    const rows = (res.defaults ?? []) as SportSeasonDefault[];
    return rows.filter((d) => d.zip3Prefix !== null && d.zip3Prefix !== undefined);
  },

  createRegionalRow: async (input) => {
    const row = (await sportSeasonDefaultsApi.create(input)) as SportSeasonDefault;
    // Refresh the matrix so regionalCount on the corresponding cell updates.
    await get().loadMatrix();
    return row;
  },

  updateRegionalRow: async (id, patch) => {
    const result = (await sportSeasonDefaultsApi.update(id, patch)) as {
      default: SportSeasonDefault;
    };
    return result.default;
  },

  deleteRegionalRow: async (id) => {
    await sportSeasonDefaultsApi.remove(id);
    // Refresh the matrix so regionalCount badge decrements.
    await get().loadMatrix();
  },
}));
